// TanStack Query hooks for dashboard metrics
import { useQuery } from '@tanstack/react-query';
import { adminApiService } from '@/services/adminApi';
import { authService } from '@/services/auth';
import { createLogger } from '@/utils/logger';
import type { DashboardMetrics, DateRange } from '@/types/analytics';

const logger = createLogger('useDashboardMetrics');

// Query Keys
export const dashboardQueryKeys = {
  all: ['dashboard'] as const,
  metrics: () => [...dashboardQueryKeys.all, 'metrics'] as const,
  overview: (dateRange?: DateRange) => [...dashboardQueryKeys.metrics(), 'overview', dateRange] as const,
};

/**
 * Hook to fetch dashboard overview metrics
 * OPTIMIZED: Improved caching and parallel loading for better performance
 */
export const useDashboardMetrics = (dateRange?: DateRange) => {
  return useQuery({
    queryKey: dashboardQueryKeys.overview(dateRange),
    queryFn: async (): Promise<DashboardMetrics> => {
      try {
        // Get the analytics overview from the admin API
        const response = await adminApiService.getDashboardMetrics(dateRange);
        
        // The response.data is already transformed by getDashboardMetrics() into DashboardMetrics format
        const dashboardMetrics = response?.data;
        
        // Only log occasionally to avoid spam (every 10th fetch)
        if (Math.random() < 0.1) {
          logger.debug('DashboardMetrics received:', {
            overview: dashboardMetrics?.overview,
            total_generations: dashboardMetrics?.overview?.total_generations,
            total_teams: dashboardMetrics?.overview?.total_teams,
            has_data: !!dashboardMetrics
          });
        }

        // Return the already-transformed dashboard metrics
        if (!dashboardMetrics) {
          throw new Error('No dashboard metrics received from API');
        }

        return dashboardMetrics;
      } catch (error) {
        logger.error('Failed to fetch dashboard metrics:', error);
        throw error;
      }
    },
    staleTime: 3 * 60 * 1000, // 3 minutes (reduced from 5 for fresher data)
    gcTime: 10 * 60 * 1000, // 10 minutes (reduced from 15 to save memory)
    refetchOnWindowFocus: false,
    retry: 2,
    enabled: !!authService.isAuthenticated(), // Only fetch when authenticated
    // Add placeholderData to show cached data immediately while refetching
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook to fetch minimal dashboard metrics for quick loading
 */
export const useDashboardMetricsMinimal = () => {
  return useQuery({
    queryKey: [...dashboardQueryKeys.all, 'minimal'],
    queryFn: async () => {
      try {
        const response = await adminApiService.getDashboardMetrics();
        return {
          total_users: response.data?.overview?.total_users || 0,
          total_generations: response.data?.overview?.total_generations || 0,
          active_users: response.data?.overview?.active_users_today || 0,
        };
      } catch (error) {
        logger.error('Failed to fetch minimal metrics:', error);
        throw error;
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: !!authService.isAuthenticated(),
  });
};